import { useEffect, useRef, useState } from "react";
import WorkspaceList from "./WorkspaceList";
import { useWorkspaceList } from "../../hooks/ui";
import { WorkspaceType } from "../../types/workspace.list.interface";
import ButtonComponent from "../Button";
import { Warning } from "@vibe/icons";
import Spinner from "../Spinner";
import { useClickOutside, useCalcHeight } from "../../hooks/ui";
import AddItem from "./additem";
import logger from "../../lib/logger";

const client_api_key = process.env.REACT_APP_CLIENT_ID;
const redirect_uri = process.env.REACT_APP_REDIRECT_URI;

interface AddItemDropdownInterfaceProps {
  centered: boolean;
  setOpen?: (value: boolean) => void;
  fullWidth: boolean;
  bigAccount: boolean;
  batchSize?: number;
  disableAll?: boolean;
  moreThan20?: boolean;
  stepsEnabled: boolean;
  /**
   * Height of the `fullWidth` variant. Defaults to the viewport (the item-menu
   * feature owns its whole iframe); a surface that stacks a header above the
   * picker passes e.g. `calc(100vh - 40px)` so the list doesn't overflow.
   */
  fullWidthHeight?: string;
}

const AddItemDropdown = ({
  centered,
  setOpen,
  fullWidth,
  bigAccount,
  batchSize = 50,
  disableAll = false,
  moreThan20,
  stepsEnabled = false,
  fullWidthHeight = "100vh",
}: AddItemDropdownInterfaceProps): JSX.Element => {
  const [retry, setRetry] = useState(false);
  const [reauth, setReauth] = useState(0);
  const dropdownRef = useRef<any>(null);
  const { workspaceListdata, isLoading, error, mutate } = useWorkspaceList(bigAccount);

  const { height, setHeight, calcHeight } = useCalcHeight();

  useEffect(() => {
    if (dropdownRef?.current) {
      setHeight(
        dropdownRef?.current?.firstChild.offsetHeight > 400
          ? 400
          : dropdownRef?.current?.firstChild.offsetHeight,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeDropdown = () => {
    setOpen?.(false);
  };

  useClickOutside(dropdownRef, closeDropdown, stepsEnabled);

  const retryFetch = async () => {
    try {
      setRetry(true);
      setReauth(reauth + 1);
      await mutate();
    } catch (error: any) {
      logger.error("Error retrying fetch boards", error);
    } finally {
      setRetry(false);
    }
  };

  const fullWidthStyle = fullWidth ? { height: fullWidthHeight } : undefined;

  if ((isLoading || retry) && bigAccount) {
    return (
      <div
        style={fullWidthStyle}
        className={`z-[1000] add-item-dropdown top-[58px] ${centered ? "left-1/2 transform -translate-x-1/2" : "right-0"} ${
          fullWidth ? "w-full" : "absolute w-[300px]"
        }  rounded bg-white px-2 overflow-y-auto overflow-x-hidden shadow-2xl`}
      >
        <div className="flex gap-2 items-center pt-4 pb-4">
          <Spinner /> Getting your workspaces
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={fullWidthStyle}
        className={`z-[1000] add-item-dropdown top-[58px] ${centered ? "left-1/2 transform -translate-x-1/2" : "right-0"} ${
          fullWidth ? "w-full" : "absolute w-[300px]"
        }  rounded bg-white px-2 overflow-y-auto overflow-x-hidden shadow-2xl`}
      >
        <div className="flex flex-col gap-2 justify-center items-center p-2">
          {reauth >= 1 ? (
            <div className="flex items-center text-center justify-center max-w-[241px]">
              <span>
                If the error persists, try authorizing again using this{" "}
                <a
                  className="hover:underline"
                  rel="noreferrer"
                  href={`https://auth.monday.com/oauth2/authorize?client_id=${client_api_key}&redirect_uri=${redirect_uri}`}
                  target="_blank"
                >
                  link
                </a>
              </span>
            </div>
          ) : (
            <>
              <div className="flex justify-center items-center">
                <Warning className="mr-1" />
                <span>An error occurred. Please try again.</span>
              </div>
            </>
          )}

          <div className="flex justify-center">
            <ButtonComponent text="Retry" className="retry-button" onClick={retryFetch} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        height: `${fullWidth ? fullWidthHeight : `${height}px`}`,
      }}
      ref={dropdownRef}
      className={`z-[1000] add-item-dropdown top-[58px] ${centered ? "left-1/2 transform -translate-x-1/2" : "right-0"} ${
        fullWidth ? "w-full" : "absolute w-[300px]"
      } rounded bg-white overflow-hidden shadow-2xl`}
    >
      <div className="overflow-y-auto overflow-x-hidden px-2 h-full">
        {/* Workspace List */}
        {bigAccount ? (
          <WorkspaceList
            data={workspaceListdata as WorkspaceType[]}
            calcHeight={calcHeight}
            options={{ batchSize, disableAll, moreThan20 }}
            onGroupSelect={closeDropdown}
          />
        ) : (
          // Small accounts
          <AddItem
            calcHeight={calcHeight}
            batchSize={batchSize}
            disableAll={disableAll}
            moreThan20={moreThan20}
            onGroupSelect={closeDropdown}
          />
        )}
      </div>
    </div>
  );
};

export default AddItemDropdown;
